/**
 * The Studio queue (/studio/, staff only): every artistic-edit request with what the person
 * asked for and their original video. For each: download the original, keep notes and a status,
 * upload the finished edit (browser → S3, straight into the person's folder), and deliver it to
 * their Exports, which also emails them.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PAPER_CSS } from "../pages/myLibrary/looks/paperStyles";
import { ROUTES } from "../../constants";
import IridescentCherry from "./IridescentCherry";
import { STUDIO, ART } from "./studioText";
import { getJson, postJson, STUDIO_QUEUE_URL, studioItemUrl } from "./studioApi";

interface Req {
  id: number;
  status: string;
  email: string;
  video_title: string;
  transcript_id: number;
  original_url: string;
  videos?: { id: number; title: string; original_url: string; seconds?: number | null }[];
  about: string;
  wants: string;
  audience: string;
  shape: string;
  will_give_feedback: boolean;
  staff_note: string;
  additions?: { at: string; text: string }[];
  created_at: string;
  creation_title: string;
}

const audienceLabel = (k: string) => ART.audiences.find((a) => a.key === k)?.label || "—";

/** Length of a video file, read in the browser (0 if it can't be read). */
const fileSeconds = (file: File): Promise<number> => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { resolve(Number.isFinite(v.duration) ? v.duration : 0); URL.revokeObjectURL(v.src); };
  v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

const putFile = (url: string, file: File, onPct: (n: number) => void): Promise<boolean> => new Promise((resolve) => {
  const xhr = new XMLHttpRequest();
  xhr.open("PUT", url);
  xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
  xhr.upload.onprogress = (e) => { if (e.lengthComputable) onPct(Math.round((e.loaded / e.total) * 100)); };
  xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
  xhr.onerror = () => resolve(false);
  xhr.send(file);
});

const RequestCard: React.FC<{ req: Req; onChange: (r: Req) => void }> = ({ req, onChange }) => {
  const [note, setNote] = useState(req.staff_note);
  const [pct, setPct] = useState<number | null>(null);
  const [key, setKey] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [title, setTitle] = useState(`Artistic edit · ${req.video_title}`);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async (patch: Partial<Req>) => {
    const { ok, data } = await postJson(studioItemUrl(req.id), patch);
    if (ok) { onChange({ ...req, ...data }); setMsg(STUDIO.saved); } else setMsg(data.message || STUDIO.error);
  };

  const upload = async (file: File) => {
    setMsg("");
    setPct(0);
    const { ok, data } = await postJson(`${studioItemUrl(req.id)}upload-url/`, { file_name: file.name });
    if (!ok) { setPct(null); setMsg(data.message || STUDIO.error); return; }
    setSeconds(await fileSeconds(file));
    if (await putFile(data.upload_url, file, setPct)) setKey(data.key);
    else { setPct(null); setMsg(STUDIO.error); }
  };

  const deliver = async () => {
    setBusy(true);
    const { ok, data } = await postJson(`${studioItemUrl(req.id)}deliver/`, { key, title, duration_seconds: seconds });
    setBusy(false);
    if (ok) onChange({ ...req, status: "delivered", creation_title: title });
    else setMsg(data.message || STUDIO.error);
  };

  const delivered = req.status === "delivered";
  return (
    <div className={`said-group st-card st-${req.status}`}>
      <div className="hd">
        <div className="ttl">{req.video_title || "—"}</div>
        <span className="st-badge">{STUDIO.status[req.status] || req.status}</span>
      </div>
      <div className="meta">{STUDIO.from} <b>{req.email}</b> · {new Date(req.created_at).toLocaleString()}</div>
      <div className="st-grid">
        <div><span>{STUDIO.wants}</span><p>{req.wants}</p></div>
        {req.about && <div><span>{STUDIO.about}</span><p>{req.about}</p></div>}
        {(req.additions || []).map((a, i) => (
          <div key={i} className="st-added"><span>{STUDIO.added(new Date(a.at).toLocaleString())}</span><p>{a.text}</p></div>
        ))}
        <div className="st-facts">
          {STUDIO.for}: <b>{audienceLabel(req.audience)}</b> · {STUDIO.shape}: <b>{req.shape}</b> · {STUDIO.feedback}: <b>{req.will_give_feedback ? "yes" : "no"}</b>
        </div>
      </div>
      <div className="st-videos">
        <span>{STUDIO.videos((req.videos || []).length || 1)}</span>
        {(req.videos || [{ id: req.transcript_id, title: req.video_title, original_url: req.original_url }]).map((v) => (
          <div key={v.id} className="st-video">
            <b>{v.title}</b>
            {"seconds" in v && v.seconds ? <em>{Math.round((v.seconds as number) / 60)} min</em> : null}
            {v.original_url && <a className="pill line" href={v.original_url} target="_blank" rel="noopener noreferrer" download>{STUDIO.original}</a>}
          </div>
        ))}
      </div>
      <div className="xrow">
        {!delivered && (
          <select value={req.status} onChange={(e) => save({ status: e.target.value })}>
            {["new", "working", "declined"].map((s) => <option key={s} value={s}>{STUDIO.status[s]}</option>)}
          </select>
        )}
      </div>
      <textarea className="st-note" rows={2} placeholder={STUDIO.note} value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== req.staff_note && save({ staff_note: note })} />
      {delivered ? (
        <div className="art-msg" style={{ padding: 0 }}>✓ {STUDIO.deliveredAs(req.creation_title)}</div>
      ) : (
        <div className="st-deliver">
          <label className="pill line st-file">
            {pct === null ? STUDIO.pickFile : key ? STUDIO.uploaded : STUDIO.uploading(pct)}
            <input type="file" accept="video/*" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          </label>
          {key && (
            <>
              <input className="xtitle" value={title} aria-label={STUDIO.editTitle} onChange={(e) => setTitle(e.target.value)} />
              <button type="button" className="pill art-go" disabled={busy} onClick={deliver}>{busy ? STUDIO.delivering : STUDIO.deliver}</button>
            </>
          )}
        </div>
      )}
      {msg && <div className="meta">{msg}</div>}
    </div>
  );
};

const StudioQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Req[] | null>(null);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = useState<"open" | "all">("open");

  useEffect(() => {
    getJson(STUDIO_QUEUE_URL).then(({ ok, status, data }) => {
      if (status === 403 || status === 401) setDenied(true);
      else if (ok) setRows(data.requests || []);
    });
  }, []);

  const shown = (rows || []).filter((r) => filter === "all" || r.status === "new" || r.status === "working");
  return (
    <div className="ccl" style={{ display: "block" }}>
      <style>{PAPER_CSS + STUDIO_CSS}</style>
      <main className="main" style={{ maxWidth: 980, margin: "0 auto" }}>
        <div className="top">
          <h1 style={{ display: "flex", alignItems: "center", gap: 12 }}><IridescentCherry size={36} /> {STUDIO.title}</h1>
          <button type="button" className="pill line" onClick={() => navigate(ROUTES.MY_LIBRARY_UPLOADS)}>← My Library</button>
        </div>
        <div className="hint">{STUDIO.sub}</div>
        {denied ? <div className="hint">{STUDIO.denied}</div> : (
          <>
            <div className="ico" style={{ marginBottom: 14 }}>
              {(["open", "all"] as const).map((k) => (
                <button type="button" key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{STUDIO.filters[k]}</button>
              ))}
            </div>
            {rows === null && <div className="meta">…</div>}
            {rows && shown.length === 0 && <div className="meta">{STUDIO.empty}</div>}
            {shown.map((r) => (
              <RequestCard key={r.id} req={r} onChange={(n) => setRows((all) => (all || []).map((x) => (x.id === n.id ? n : x)))} />
            ))}
          </>
        )}
      </main>
    </div>
  );
};

const STUDIO_CSS = `
.ccl .st-badge{font-size:12px;font-weight:800;border-radius:999px;padding:4px 10px;background:#f3e8ff;color:#7c3aed}
.ccl .st-delivered .st-badge{background:#dcfce7;color:#15803d}
.ccl .st-declined{opacity:.6}
.ccl .st-grid{display:grid;gap:10px;margin:12px 0}
.ccl .st-grid span{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#8a7f8c}
.ccl .st-grid p{margin:2px 0 0;font-size:15px;line-height:1.5;white-space:pre-wrap}
.ccl .st-facts{font-size:13px;color:#6b6070}
.ccl .st-note{width:100%;margin-top:10px;border:1px solid #ebe1d8;border-radius:10px;padding:8px 12px;font:inherit;font-size:13px}
.ccl .st-deliver{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-top:10px}
.ccl .st-file{cursor:pointer}
.ccl a.pill{text-decoration:none}
.ccl .st-added{background:#f3e8ff;border-radius:10px;padding:8px 12px}
.ccl .st-added span{color:#7c3aed!important}
.ccl .st-videos{display:grid;gap:6px;margin:4px 0 10px}
.ccl .st-videos>span{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#8a7f8c}
.ccl .st-video{display:flex;align-items:center;gap:10px;background:#faf6f2;border-radius:10px;padding:8px 10px}
.ccl .st-video b{flex:1;font-size:14px}
.ccl .st-video em{font-style:normal;font-size:12px;color:#8a7f8c}
.ccl .st-video .pill{padding:6px 12px;font-size:13px}
`;

export default StudioQueuePage;
